import { useState, useRef, useEffect, useCallback } from "react";
import { Select } from "antd";
import ReactMarkdown from "react-markdown";
import { askStream, getChatHistory } from "../../../services/chat";
import { listFiles } from "../../../services/files";
import { startDeepResearch } from "../../../services/deepResearch";
import { jobTypes as jobTypesApi, workStages as workStagesApi, departments as departmentsApi } from "../../../services/admin";
import { listProjects } from "../../../services/projects";
import { getIndexStatusLabel } from "../../../utils/fileStatus";
import { formatBeijingDateTime, formatBeijingMonthDayTime, formatBeijingTime } from "../../../utils/datetime";
import type { ChatSource, ChatHistoryItem, ProgressData } from "../../../services/chat";
import type { DeepResearchProgress } from "../../../services/deepResearch";
import type { FileItem } from "../../../services/files";
import RIcon from "../../../components/RIcon";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * 折叠《A.docx / A.docx / A.docx》成《A.docx》。LLM 偶尔会把同一文件名重复输出。
 * 同时把"来源文件如下"段落里完全相同的连续行合并成一行。
 */
function dedupSourceFilenames(text: string): string {
  if (!text) return text;
  const folded = text.replace(/《([^》]+)》/g, (_m, inner: string) => {
    const parts = inner.split("/").map((p) => p.trim()).filter(Boolean);
    if (parts.length <= 1) return `《${inner}》`;
    const seen: string[] = [];
    for (const p of parts) {
      if (!seen.includes(p)) seen.push(p);
    }
    return `《${seen.join(" / ")}》`;
  });
  // 来源段落连续重复行去重
  const lines = folded.split("\n");
  const out: string[] = [];
  let inSrc = false;
  let prev = "";
  for (const line of lines) {
    if (line.includes("来源文件如下")) {
      inSrc = true;
      out.push(line);
      prev = "";
      continue;
    }
    if (inSrc) {
      const stripped = line.trim();
      if (stripped && stripped === prev.trim()) continue;
      prev = line;
    }
    out.push(line);
  }
  return out.join("\n");
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface Source {
  file_id?: number;
  name: string;
  type: string;
  content?: string;
  relevance?: number;
}

interface FileAnswer {
  file_id: number;
  file_name: string;
  content: string;
  method: string;
  streaming: boolean;
  done: boolean;
}

// ─── Markdown Renderer ───────────────────────────────────────────────────────

function MarkdownMessage({ content, sources: _sources }: { content: string; sources?: Source[] }) {
  // 正文里的 "《文件名》" 不再做 markdown 链接化，用户反馈"去掉文字链接保留绿色高亮即可"；
  // 绿色高亮通过下方 prose-em/strong + 自定义 strong 颜色实现，另外 sources chips 仍可点击下载
  const rendered = content;

  return (
    <div className="prose prose-sm max-w-none text-[#4A5568]
      prose-headings:text-[#1A2B3C] prose-headings:font-semibold
      prose-h2:text-sm prose-h2:mt-3 prose-h2:mb-1.5
      prose-h3:text-sm prose-h3:mt-2 prose-h3:mb-1
      prose-p:text-sm prose-p:leading-relaxed prose-p:my-1
      prose-li:text-sm prose-li:my-0.5
      prose-strong:text-[#1A2B3C]
      prose-a:text-[#0EA5A0] prose-a:font-medium prose-a:no-underline hover:prose-a:underline
      prose-blockquote:border-[#0EA5A0] prose-blockquote:text-[#718096] prose-blockquote:text-xs
      prose-table:text-xs
      prose-th:bg-[#F4F8FB] prose-th:px-2 prose-th:py-1 prose-th:text-[#1A2B3C]
      prose-td:px-2 prose-td:py-1 prose-td:border-b prose-td:border-gray-100
      prose-code:text-xs prose-code:bg-[#F4F8FB] prose-code:px-1 prose-code:rounded
      prose-hr:border-gray-200 prose-hr:my-3
    ">
      <ReactMarkdown>{rendered}</ReactMarkdown>
    </div>
  );
}

// ─── Source Tags ──────────────────────────────────────────────────────────────

const fileTypeIcon: Record<string, string> = {
  pdf: "ri-file-pdf-line",
  word: "ri-file-word-line",
  excel: "ri-file-excel-line",
  docx: "ri-file-word-line",
  xlsx: "ri-file-excel-line",
};

function SourceTags({ sources, visible, onSelect, selectedName }: {
  sources: Source[];
  visible: boolean;
  onSelect: (src: Source) => void;
  selectedName?: string;
}) {
  // 按"折叠后的文件名"去重：同名/重复名只显示一次，相关度取最高
  const dedupMap = new Map<string, Source>();
  for (const src of sources) {
    const cleanName = (src.name || "").split("/").map((p) => p.trim()).filter(Boolean)[0] || src.name;
    const prev = dedupMap.get(cleanName);
    if (!prev || (src.relevance ?? 0) > (prev.relevance ?? 0)) {
      dedupMap.set(cleanName, { ...src, name: cleanName });
    }
  }
  const dedupedSources = Array.from(dedupMap.values());
  return (
    <div
      className="flex flex-wrap gap-1.5 mt-1 overflow-hidden transition-all duration-500"
      style={{ maxHeight: visible ? "120px" : "0px", opacity: visible ? 1 : 0 }}
    >
      {dedupedSources.map((src, i) => {
        const ext = src.name.split(".").pop()?.toLowerCase() || "";
        return (
          <button
            key={i}
            onClick={() => onSelect(src)}
            title={src.relevance !== undefined ? `相关度 ${Math.round(src.relevance * 100)}%（数值越高越相关，基于向量相似度计算）` : src.name}
            className={`flex items-center gap-1 border text-[#4A5568] text-xs px-2 py-1 rounded-lg cursor-pointer transition-colors whitespace-nowrap ${
              selectedName === src.name
                ? "bg-[#0EA5A0]/10 border-[#0EA5A0] text-[#0EA5A0]"
                : "bg-white border-gray-200 hover:border-[#0EA5A0]"
            }`}
          >
            <RIcon name="file-line" className={`${fileTypeIcon[ext] || ""} text-[#0EA5A0]`} />
            <span className="max-w-32 truncate">{src.name}</span>
            {src.relevance !== undefined && (
              <span className="text-[10px] text-[#718096]" title="相关度（向量相似度），越高越相关">
                {Math.round(src.relevance * 100)}%
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── Per-File Answer Card ────────────────────────────────────────────────────

function FileAnswerCard({ fa, defaultOpen }: { fa: FileAnswer; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  // 兜底：如果 file_name 本身被错误地拼成 "A / A" 这种重复，折叠成单个
  const cleanName = (fa.file_name || "").split("/").map((p) => p.trim()).filter(Boolean)[0] || fa.file_name;
  const ext = cleanName.split(".").pop()?.toLowerCase() || "";
  const methodLabel = fa.method === "tree_search" ? "树搜索" : fa.method === "content_text" ? "全文" : "回退";

  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden mb-2">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-[#F4F8FB] hover:bg-[#EDF2F7] transition-colors cursor-pointer text-left"
      >
        <RIcon name="file-line" className={`${fileTypeIcon[ext] || ""} text-[#0EA5A0]`} />
        <span className="text-xs font-medium text-[#1A2B3C] flex-1 truncate">{cleanName}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#0EA5A0]/15 text-[#0EA5A0] font-medium">{methodLabel}</span>
        {fa.streaming && (
          <span className="w-1.5 h-1.5 bg-[#0EA5A0] rounded-full animate-pulse" />
        )}
        <i className={`ri-arrow-${open ? "up" : "down"}-s-line text-[#718096] text-sm`} />
      </button>
      {open && (
        <div className="px-3 py-2.5 bg-white">
          <MarkdownMessage
            content={dedupSourceFilenames(fa.content)}
            sources={[{ file_id: fa.file_id, name: cleanName, type: ext }]}
          />
          {fa.streaming && fa.content && (
            <span
              className="inline-block w-0.5 h-4 bg-[#0EA5A0] rounded-full ml-0.5 align-middle"
              style={{ animation: "cursorBlink 0.8s steps(1) infinite" }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Expandable File List (progress 步骤中展开文件列表) ─────────────────────

function ExpandableFileList({ label, items, defaultOpen = false }: {
  label: string;
  items: { name: string; ext: string }[];
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="ml-5 mt-1.5">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 px-2 py-1 text-xs text-[#0EA5A0] hover:bg-[#0EA5A0]/10 rounded-lg cursor-pointer transition-colors border border-[#0EA5A0]/30">
        <i className={`ri-arrow-${open ? "down" : "right"}-s-line text-sm`} />
        <span>{label}</span>
      </button>
      {open && (
        <div className="mt-1.5 flex flex-wrap gap-1.5 ml-1">
          {items.map((item, i) => {
            const ext = item.ext.toLowerCase();
            return (
              <span key={i} className="flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded-lg text-xs text-[#4A5568] hover:border-[#0EA5A0] transition-colors">
                <RIcon name="file-line" className={`${fileTypeIcon[ext] || ""} text-[#0EA5A0]`} />
                <span className="max-w-40 truncate">{item.name}</span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Collapsible RAG Progress Steps ──────────────────────────────────────────

function CollapsibleProgressSteps({ steps, streaming }: { steps: ProgressData[]; streaming: boolean }) {
  const [open, setOpen] = useState(streaming);
  // Auto-open while streaming, allow manual toggle
  useEffect(() => { if (streaming) setOpen(true); }, [streaming]);

  if (steps.length === 0) return null;

  return (
    <div className="mb-2 border border-gray-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-[#F4F8FB] hover:bg-[#EDF2F7] transition-colors cursor-pointer text-left"
      >
        <RIcon name="route-line" className="text-[#0EA5A0] text-sm" />
        <span className="text-xs font-medium text-[#1A2B3C] flex-1">RAG 检索过程</span>
        {streaming && <span className="w-1.5 h-1.5 bg-[#0EA5A0] rounded-full animate-pulse" />}
        <span className="text-[10px] text-[#718096]">{steps.length} 步</span>
        <i className={`ri-arrow-${open ? "up" : "down"}-s-line text-[#718096] text-sm`} />
      </button>
      {open && (
        <div className="px-3 py-2.5 bg-white space-y-1.5">
          {steps.map((p, i) => {
            const isLast = i === steps.length - 1;
            const icon = p.step === "loading" ? "ri-loader-4-line" : p.step === "memory_loaded" ? "ri-history-line" : p.step === "vector_scope" ? "ri-database-2-line" : p.step === "vector_search" ? "ri-radar-line" : p.step === "files_loaded" ? "ri-file-list-3-line" : p.step === "selecting" ? "ri-brain-line" : p.step === "selection_reasoning" ? "ri-brain-line" : p.step === "selected" ? "ri-check-double-line" : p.step === "pageindex_reasoning" ? "ri-cpu-line" : p.step === "tree_search" ? "ri-node-tree" : p.step === "tree_found" ? "ri-check-line" : p.step === "fallback_reasoning" ? "ri-article-line" : p.step === "fallback" ? "ri-file-text-line" : p.step === "generating" ? "ri-sparkling-line" : p.step === "synthesizing" ? "ri-links-line" : "ri-loader-4-line";
            return (
              <div key={i}>
                <div className={`flex items-start gap-2 ${isLast && streaming ? "text-[#0EA5A0]" : "text-[#718096]"}`}>
                  <i className={`${icon} text-xs mt-0.5 flex-shrink-0 ${isLast && streaming ? "animate-spin" : ""}`} />
                  <span className="text-xs leading-relaxed">{p.message}</span>
                </div>
                {p.step === "files_loaded" && p.files && (
                  <ExpandableFileList
                    label={`查看全部 ${p.files.length} 个文档`}
                    items={p.files.map((f) => ({ name: f.name, ext: f.type }))}
                  />
                )}
                {p.step === "selected" && p.selected_files && (
                  <ExpandableFileList
                    label={`查看选中的 ${p.selected_files.length} 个文档`}
                    items={p.selected_files.map((name) => ({ name, ext: name.split(".").pop() || "" }))}
                    defaultOpen
                  />
                )}
                {(p.step === "selection_reasoning" || p.step === "pageindex_reasoning" || p.step === "fallback_reasoning") && (
                  <div className={`ml-5 mt-1.5 rounded-lg border p-2.5 ${p.step === "selection_reasoning" ? "border-sky-100 bg-sky-50" : p.step === "pageindex_reasoning" ? "border-violet-100 bg-violet-50" : "border-amber-100 bg-amber-50"}`}>
                    {p.node_ids && p.node_ids.length > 0 && (
                      <div className="mb-1.5 flex flex-wrap gap-1">
                        {p.node_ids.map((nodeId) => (
                          <span key={nodeId} className="rounded-full border border-violet-200 bg-white px-2 py-0.5 text-[10px] text-violet-600">
                            节点 {nodeId}
                          </span>
                        ))}
                      </div>
                    )}
                    {!!p.thinking && (
                      <p className="whitespace-pre-wrap text-[11px] leading-relaxed text-[#4A5568]">{p.thinking}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Doc Preview Panel ────────────────────────────────────────────────────────

function DocPreviewPanel({ source, onClose }: { source: Source; onClose: () => void }) {
  const ext = source.name.split(".").pop()?.toLowerCase() || "";
  return (
    <div className="w-72 flex-shrink-0 bg-white border-l border-gray-100 flex flex-col overflow-hidden animate-slideInRight">
      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .animate-slideInRight { animation: slideInRight 0.22s ease; }
      `}</style>
      <div className="px-4 py-3 border-b border-gray-100 flex items-start gap-2.5">
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
          <RIcon name="file-line" className={`${fileTypeIcon[ext] || ""} text-xl text-[#0EA5A0]`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-[#1A2B3C] leading-tight line-clamp-2">{source.name}</p>
          {source.relevance !== undefined && (
            <span className="inline-block text-xs bg-[#0EA5A0]/15 text-[#0EA5A0] px-1.5 py-0.5 rounded-full mt-1 font-medium">
              相关度 {Math.round(source.relevance * 100)}%
            </span>
          )}
        </div>
        <button onClick={onClose} className="w-6 h-6 flex items-center justify-center text-[#718096] hover:text-[#1A2B3C] hover:bg-gray-100 rounded transition-colors flex-shrink-0 cursor-pointer">
          <RIcon name="close-line" className="text-sm" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {source.content ? (
          <div className="bg-[#F4F8FB] rounded-xl p-3.5">
            <div className="flex items-center gap-1.5 mb-2.5">
              <RIcon name="file-text-line" className="text-[#0EA5A0] text-sm" />
              <span className="text-xs font-semibold text-[#1A2B3C]">匹配内容</span>
            </div>
            <p className="text-xs text-[#4A5568] leading-relaxed whitespace-pre-wrap">{source.content}</p>
          </div>
        ) : (
          <div className="text-center py-8">
            <RIcon name="file-text-line" className="text-3xl text-gray-300" />
            <p className="text-xs text-[#718096] mt-2">无预览内容</p>
          </div>
        )}
      </div>
      <div className="px-4 py-3 border-t border-gray-100 space-y-2">
        <button
          onClick={() => source.file_id && (window.location.href = `/workbench/files?id=${source.file_id}`)}
          className="w-full h-8 text-xs border border-[#0EA5A0] text-[#0EA5A0] hover:bg-[#0EA5A0]/5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          <RIcon name="external-link-line" className="mr-1" />在文件管理中查看
        </button>
      </div>
    </div>
  );
}

// ─── Highlight Text ───────────────────────────────────────────────────────────

function HighlightText({ text, keyword }: { text: string; keyword: string }) {
  if (!keyword.trim()) return <>{text}</>;
  const regex = new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-[#AAFF45]/40 text-[#1A2B3C] rounded-sm px-0.5">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

// ─── Chat Message Type ────────────────────────────────────────────────────────

interface ChatMessage {
  id: string;
  role: "user" | "ai";
  content: string;
  time: string;
  sources?: Source[];
  streaming?: boolean;
  fileAnswers?: FileAnswer[];
  hasSynthesis?: boolean;
  progressSteps?: ProgressData[];
  researchProgress?: DeepResearchProgress;
  statusText?: string;
}

interface HistoryEntry {
  id: string;
  sessionId?: string | null;
  parentId?: number | null;
  title: string;
  preview: string;
  time: string;
  group: string;
  question: string;
  answer: string;
  sources?: Source[];
}

const DEEP_RESEARCH_GUIDE = `已进入 DeepResearch 模式。

请直接描述：
1. 研究主题或核心问题
2. 想得到的结果
3. 重点关注维度
4. 是否有时效要求

我会自动开始多轮联网搜索、结合权限内文件检索，并输出结构化研究文档。`;

function formatHistoryGroup(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "今天";
  if (diffDays === 1) return "昨天";
  if (diffDays <= 7) return "本周";
  return "更早";
}

// ─── Main Page ────────────────────────────────────────────────────────────────

interface FilterOption { id: number; name: string; }

function matchesFileFilters(
  file: FileItem,
  filters: {
    departmentId?: number;
    jobTypeId?: number;
    workStageId?: number;
    projectId?: number;
    category?: string;
  }
) {
  if (filters.departmentId && file.department_id !== filters.departmentId) return false;
  if (filters.jobTypeId && file.job_type_id !== filters.jobTypeId) return false;
  if (filters.workStageId && file.work_stage_id !== filters.workStageId) return false;
  if (filters.projectId && file.project_id !== filters.projectId) return false;
  if (filters.category && file.category !== filters.category) return false;
  return true;
}

// sessionStorage key（随 tab 生存，切页面回来还在；关闭 tab 才清）
const CHAT_STORAGE_KEY = "gsd_ai_chat_state_v1";

function loadStoredChatState(): {
  chatMessages: ChatMessage[];
  researchMessages: ChatMessage[];
  currentSessionId: string | null;
  currentParentId: number | null;
} {
  try {
    const raw = typeof window !== "undefined" ? window.sessionStorage.getItem(CHAT_STORAGE_KEY) : null;
    if (!raw) return { chatMessages: [], researchMessages: [], currentSessionId: null, currentParentId: null };
    const parsed = JSON.parse(raw);
    return {
      chatMessages: Array.isArray(parsed.chatMessages) ? parsed.chatMessages : [],
      researchMessages: Array.isArray(parsed.researchMessages) ? parsed.researchMessages : [],
      currentSessionId: parsed.currentSessionId ?? null,
      currentParentId: parsed.currentParentId ?? null,
    };
  } catch {
    return { chatMessages: [], researchMessages: [], currentSessionId: null, currentParentId: null };
  }
}

function persistChatState(state: {
  chatMessages: ChatMessage[];
  researchMessages: ChatMessage[];
  currentSessionId: string | null;
  currentParentId: number | null;
}) {
  try {
    if (typeof window === "undefined") return;
    // 对于还在 streaming 的消息，保留其文本但不保留 streaming=true（切回后不会假装还在流）
    const snapshot = {
      chatMessages: state.chatMessages.map((m) => ({ ...m, streaming: false })),
      researchMessages: state.researchMessages.map((m) => ({ ...m, streaming: false })),
      currentSessionId: state.currentSessionId,
      currentParentId: state.currentParentId,
    };
    window.sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // 超 quota / 其他异常忽略
  }
}

export default function AiChatPage() {
  const _initial = loadStoredChatState();
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(_initial.chatMessages);
  const [researchMessages, setResearchMessages] = useState<ChatMessage[]>(_initial.researchMessages);
  const [streamingId, setStreamingId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(_initial.currentSessionId);
  const [currentParentId, setCurrentParentId] = useState<number | null>(_initial.currentParentId);
  const [historySearch, setHistorySearch] = useState("");
  const [selectedSource, setSelectedSource] = useState<Source | null>(null);
  const [fileCount, setFileCount] = useState(0);
  const [allFiles, setAllFiles] = useState<FileItem[]>([]);
  const [filesListOpen, setFilesListOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [jobTypeOptions, setJobTypeOptions] = useState<FilterOption[]>([]);
  const [projectOptions, setProjectOptions] = useState<FilterOption[]>([]);
  const [workStageOptions, setWorkStageOptions] = useState<FilterOption[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<FilterOption[]>([]);
  const [filterDepartmentId, setFilterDepartmentId] = useState<number | undefined>(undefined);
  const [filterJobTypeId, setFilterJobTypeId] = useState<number | undefined>(undefined);
  const [filterWorkStageId, setFilterWorkStageId] = useState<number | undefined>(undefined);
  const [filterProjectId, setFilterProjectId] = useState<number | undefined>(undefined);
  const [filterCategory, setFilterCategory] = useState("");
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<FileItem[]>([]);
  const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
  const [deepResearchMode, setDeepResearchMode] = useState(false);
  const [hasShownDeepResearchGuide, setHasShownDeepResearchGuide] = useState(false);
  const messages = deepResearchMode ? researchMessages : chatMessages;
  const setMessages = deepResearchMode ? setResearchMessages : setChatMessages;
  const bottomRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streamingId]);

  // 自动把消息列表持久化到 sessionStorage，切页面回来能恢复
  useEffect(() => {
    persistChatState({ chatMessages, researchMessages, currentSessionId, currentParentId });
  }, [chatMessages, researchMessages, currentSessionId, currentParentId]);

  useEffect(() => {
    if (!deepResearchMode || hasShownDeepResearchGuide) return;
    const time = formatBeijingTime(new Date());
    setResearchMessages((prev) => [...prev, {
      id: `guide-${Date.now()}`,
      role: "ai",
      content: DEEP_RESEARCH_GUIDE,
      time,
    }]);
    setHasShownDeepResearchGuide(true);
  }, [deepResearchMode, hasShownDeepResearchGuide]);

  const loadHistory = useCallback(() => {
    getChatHistory(0, 50).then((items: ChatHistoryItem[]) => {
      setHistory(items.map((item) => ({
        id: String(item.id),
        sessionId: item.session_id || null,
        parentId: item.parent_id ?? null,
        title: item.question.slice(0, 30) + (item.question.length > 30 ? "..." : ""),
        preview: item.answer.slice(0, 60) + (item.answer.length > 60 ? "..." : ""),
        time: item.created_at, group: formatHistoryGroup(item.created_at),
        question: item.question, answer: item.answer,
        sources: (item.sources || []).map((src) => ({
          file_id: src.file_id,
          name: src.file_name,
          type: src.type,
          content: src.content,
          relevance: src.relevance,
        })),
      })));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    listFiles(0, 2000).then((files) => {
      setFileCount(files.length);
      setAllFiles(files);
      const cats = Array.from(new Set(files.map((f) => f.category).filter(Boolean))) as string[];
      setCategoryOptions(cats.sort());
    }).catch(() => {});
    jobTypesApi.list().then(setJobTypeOptions).catch(() => {});
    workStagesApi.list().then((items) => setWorkStageOptions(items.map((item) => ({ id: item.id, name: item.name })))).catch(() => {});
    listProjects().then(setProjectOptions).catch(() => {});
    departmentsApi.list().then((items) => setDepartmentOptions(items.map((d) => ({ id: d.id, name: d.name })))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!filterDepartmentId && !filterJobTypeId && !filterWorkStageId && !filterProjectId && !filterCategory) {
      setFilteredFiles([]); setSelectedFileIds([]); return;
    }
    const nextFiles = allFiles.filter((file) =>
      matchesFileFilters(file, {
        departmentId: filterDepartmentId,
        jobTypeId: filterJobTypeId,
        workStageId: filterWorkStageId,
        projectId: filterProjectId,
        category: filterCategory || undefined,
      })
    );
    setFilteredFiles(nextFiles);
    setSelectedFileIds(nextFiles.map((file) => file.id));
  }, [allFiles, filterDepartmentId, filterJobTypeId, filterWorkStageId, filterProjectId, filterCategory]);

  const handleSend = async () => {
    if (!input.trim() || loading || streamingId) return;
    const question = input.trim();
    const now = formatBeijingTime(new Date());

    const userMsg: ChatMessage = { id: `u-${Date.now()}`, role: "user", content: question, time: now };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    // 立即在历史侧栏插入临时条目（乐观更新），onDone 后会被真实数据替换
    const tempHistoryId = `tmp-${Date.now()}`;
    setHistory((prev) => [
      {
        id: tempHistoryId,
        sessionId: currentSessionId,
        parentId: currentParentId,
        title: question.slice(0, 30) + (question.length > 30 ? "..." : ""),
        preview: "正在生成回答...",
        time: new Date().toISOString(),
        group: "今天",
        question,
        answer: "",
        sources: [],
      },
      ...prev,
    ]);

    const aiId = `ai-${Date.now()}`;
    setMessages((prev) => [...prev, {
      id: aiId, role: "ai", content: "", time: now,
      sources: [], streaming: true, fileAnswers: [], hasSynthesis: false, progressSteps: [],
    }]);

    const abort = new AbortController();
    abortRef.current = abort;
    const fileIdsToSend = selectedFileIds.length > 0 ? selectedFileIds : undefined;

    try {
      await askStream(question, {
        onProgress: (data: ProgressData) => {
          setMessages((prev) => prev.map((m) =>
            m.id === aiId
              ? {
                  ...m,
                  progressSteps:
                    (() => {
                      const current = m.progressSteps || [];
                      const last = current[current.length - 1];
                      if (
                        last &&
                        last.step === data.step &&
                        last.message === data.message &&
                        last.file_name === data.file_name
                      ) {
                        return current;
                      }
                      return [...current, data];
                    })(),
                }
              : m
          ));
        },
        onFileStart: (data) => {
          setLoading(false);
          setStreamingId(aiId);
          setMessages((prev) => prev.map((m) => {
            if (m.id !== aiId) return m;
            // 同 file_id 已经在 fileAnswers 里就不再新增，避免逐文件解析时重复展示同一来源文件
            const existing = (m.fileAnswers || []).find((fa) => fa.file_id === data.file_id);
            if (existing) {
              return {
                ...m,
                fileAnswers: (m.fileAnswers || []).map((fa) =>
                  fa.file_id === data.file_id ? { ...fa, streaming: true, done: false, method: data.method || fa.method } : fa,
                ),
              };
            }
            const fa: FileAnswer = { file_id: data.file_id, file_name: data.file_name, content: "", method: data.method, streaming: true, done: false };
            return { ...m, fileAnswers: [...(m.fileAnswers || []), fa] };
          }));
        },
        onFileContent: (data) => {
          setMessages((prev) => prev.map((m) => {
            if (m.id !== aiId) return m;
            const fas = (m.fileAnswers || []).map((fa) =>
              fa.file_id === data.file_id ? { ...fa, content: fa.content + data.text } : fa
            );
            return { ...m, fileAnswers: fas };
          }));
        },
        onFileDone: (data) => {
          setMessages((prev) => prev.map((m) => {
            if (m.id !== aiId) return m;
            const fas = (m.fileAnswers || []).map((fa) =>
              fa.file_id === data.file_id ? { ...fa, streaming: false, done: true, method: data.method } : fa
            );
            return { ...m, fileAnswers: fas };
          }));
        },
        onContent: (text: string) => {
          setMessages((prev) => prev.map((m) =>
            m.id === aiId ? { ...m, content: m.content + text, hasSynthesis: true } : m
          ));
          setLoading(false);
          setStreamingId(aiId);
        },
        onSources: (docs: ChatSource[]) => {
          const sources: Source[] = docs.map((d) => ({ file_id: d.file_id, name: d.file_name, type: d.type, content: d.content, relevance: d.relevance }));
          setMessages((prev) => prev.map((m) => m.id === aiId ? { ...m, sources } : m));
        },
        onDone: (data) => {
          if (data.session_id) setCurrentSessionId(data.session_id);
          if (typeof data.message_id === "number") setCurrentParentId(data.message_id);
          setStreamingId(null);
          setLoading(false);
          setMessages((prev) => prev.map((m) => {
            if (m.id !== aiId) return m;
            const fallbackContent =
              !m.content && (m.fileAnswers?.length || 0) === 1
                ? (m.fileAnswers?.[0]?.content || "")
                : m.content;
            // 折叠《A.docx / A.docx》-> 《A.docx》：兜底防 LLM 把同一文件名重复输出
            const dedupedContent = dedupSourceFilenames(fallbackContent);
            const dedupedFAs = (m.fileAnswers || []).map((fa) => ({
              ...fa,
              content: dedupSourceFilenames(fa.content),
            }));
            return { ...m, streaming: false, content: dedupedContent, fileAnswers: dedupedFAs };
          }));
          loadHistory();
        },
        onError: (message: string) => {
          setLoading(false); setStreamingId(null);
          setMessages((prev) => prev.map((m) =>
            m.id === aiId ? { ...m, content: `错误: ${message}`, streaming: false } : m
          ));
        },
      }, { file_ids: fileIdsToSend, signal: abort.signal, session_id: currentSessionId, parent_id: currentParentId });
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        setLoading(false); setStreamingId(null);
        setMessages((prev) => prev.map((m) =>
          m.id === aiId ? { ...m, content: `请求失败: ${(err as Error).message}`, streaming: false } : m
        ));
      }
    }
  };

  const handleDeepResearch = async () => {
    if (!input.trim() || loading || streamingId) return;
    const question = input.trim();
    const now = formatBeijingTime(new Date());

    const userMsg: ChatMessage = { id: `u-${Date.now()}`, role: "user", content: `🔬 ${question}`, time: now };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    const aiId = `ai-${Date.now()}`;
    const fileIdsToSend = selectedFileIds.length > 0 ? selectedFileIds : undefined;
    const rp: DeepResearchProgress = {
      findings: [],
      webResults: [],
      searchRounds: [],
      selectedFileCount: selectedFileIds.length,
      stageLogs: [],
    };

    setMessages((prev) => [...prev, {
      id: aiId,
      role: "ai",
      content: "",
      time: now,
      streaming: true,
      researchProgress: rp,
      statusText: "正在启动深度研究...",
    }]);

    try {
      let fullContent = "";
      const pushStageLog = (message: string, extras?: { step?: string; round?: number; subQuestion?: string }) => {
        rp.stageLogs = [
          ...(rp.stageLogs || []),
          {
            step: extras?.step,
            round: extras?.round,
            subQuestion: extras?.subQuestion,
            message,
            at: formatBeijingTime(new Date(), { withSeconds: true }),
          },
        ].slice(-40);
      };
      await startDeepResearch(
        {
          question,
          file_ids: fileIdsToSend,
          max_rounds: 3,
          enable_web_search: true,
          search_results_per_query: 3,
        },
        (payload) => {
          const data = (payload.data || {}) as Record<string, any>;

          if (payload.event === "status") {
            rp.currentRound = data.round || rp.currentRound;
            rp.currentSubQuestion = data.sub_question || rp.currentSubQuestion;
            if (data.message) {
              pushStageLog(String(data.message), {
                step: data.step,
                round: data.round,
                subQuestion: data.sub_question,
              });
            }
            setMessages((prev) => prev.map((m) =>
              m.id === aiId ? { ...m, statusText: data.message, researchProgress: { ...rp } } : m
            ));
            return;
          }

          if (payload.event === "research_plan") {
            rp.planQuestion = data.question;
            rp.subQuestions = data.sub_questions || rp.subQuestions;
            rp.totalRounds = data.max_rounds || rp.totalRounds;
            rp.model = data.model || rp.model;
            pushStageLog("研究计划已生成", { step: "research_plan" });
            setMessages((prev) => prev.map((m) =>
              m.id === aiId ? { ...m, researchProgress: { ...rp } } : m
            ));
            return;
          }

          if (payload.event === "sub_questions") {
            rp.subQuestions = data.questions || [];
            pushStageLog(`已拆分 ${rp.subQuestions.length || 0} 个子问题`, { step: "sub_questions" });
            setMessages((prev) => prev.map((m) =>
              m.id === aiId ? { ...m, researchProgress: { ...rp } } : m
            ));
            return;
          }

          if (payload.event === "search_plan") {
            rp.currentRound = data.round || rp.currentRound;
            rp.currentSubQuestion = data.sub_question || rp.currentSubQuestion;
            rp.currentQueries = data.queries || [];
            rp.searchRounds = [
              ...(rp.searchRounds || []).filter(
                (item) => !(item.round === data.round && item.subQuestion === data.sub_question)
              ),
              {
                round: data.round,
                subQuestion: data.sub_question,
                focus: data.focus,
                queries: data.queries || [],
                results: [],
              },
            ];
            pushStageLog(
              `第 ${rp.currentRound || 1} 轮检索规划：${rp.currentQueries?.join("；") || "无查询词"}`,
              { step: "search_plan", round: rp.currentRound, subQuestion: rp.currentSubQuestion }
            );
            setMessages((prev) => prev.map((m) =>
              m.id === aiId
                ? { ...m, statusText: `正在规划第 ${data.round || 1} 轮搜索...`, researchProgress: { ...rp } }
                : m
            ));
            return;
          }

          if (payload.event === "search_query") {
            rp.currentSubQuestion = data.sub_question || rp.currentSubQuestion;
            rp.currentQueries = [data.query];
            pushStageLog(`执行搜索：${data.query}${data.provider ? `（${data.provider}）` : ""}`, {
              step: "search_query",
              round: data.round,
              subQuestion: rp.currentSubQuestion,
            });
            setMessages((prev) => prev.map((m) =>
              m.id === aiId
                ? { ...m, statusText: `正在搜索：${data.query}`, researchProgress: { ...rp } }
                : m
            ));
            return;
          }

          if (payload.event === "web_results") {
            const nextWebResults = [...(rp.webResults || [])];
            const existingIndex = nextWebResults.findIndex((item) => item.query === data.query);
            const value = { query: data.query, provider: data.provider, results: data.results || [] };
            if (existingIndex >= 0) nextWebResults[existingIndex] = value;
            else nextWebResults.push(value);
            rp.webResults = nextWebResults.slice(-6);
            rp.searchRounds = (rp.searchRounds || []).map((item) =>
              item.round === data.round && item.subQuestion === data.sub_question
                ? {
                    ...item,
                    results: [
                      ...((item.results || []).filter((result) => result.query !== data.query)),
                      value,
                    ],
                  }
                : item
            );
            pushStageLog(
              `搜索命中 ${Array.isArray(data.results) ? data.results.length : 0} 条：${data.query}${data.provider ? `（${data.provider}）` : ""}`,
              { step: "web_results", round: data.round, subQuestion: data.sub_question }
            );
            setMessages((prev) => prev.map((m) =>
              m.id === aiId ? { ...m, researchProgress: { ...rp } } : m
            ));
            return;
          }

          if (payload.event === "finding") {
            rp.currentRound = data.round || rp.currentRound;
            rp.currentSubQuestion = data.sub_question || rp.currentSubQuestion;
            rp.findings = [
              ...(rp.findings || []),
              {
                subQuestion: data.sub_question,
                round: data.round,
                totalRounds: data.total_rounds,
                summary: data.summary,
                keyPoints: data.key_points || [],
                queries: data.queries || [],
                hasDocs: data.has_doc,
                hasWeb: data.has_web,
                sources: data.sources || [],
              },
            ];
            rp.searchRounds = (rp.searchRounds || []).map((item) =>
              item.round === data.round && item.subQuestion === data.sub_question
                ? { ...item, summary: data.summary }
                : item
            );
            pushStageLog(`阶段总结完成：第 ${data.round}/${data.total_rounds} 轮`, {
              step: "finding",
              round: data.round,
              subQuestion: data.sub_question,
            });
            setMessages((prev) => prev.map((m) =>
              m.id === aiId ? { ...m, researchProgress: { ...rp } } : m
            ));
            return;
          }

          if (payload.event === "thinking") {
            const nextThinking = `${rp.thinkingText || ""}${String(data.text || "")}`.slice(-4000);
            rp.thinkingText = nextThinking;
            rp.thinkingPhase = data.phase || rp.thinkingPhase;
            setMessages((prev) => prev.map((m) =>
              m.id === aiId ? { ...m, researchProgress: { ...rp } } : m
            ));
            return;
          }

          if (payload.event === "content") {
            fullContent += data.text || "";
            setMessages((prev) => prev.map((m) =>
              m.id === aiId ? { ...m, content: fullContent, statusText: undefined, researchProgress: { ...rp } } : m
            ));
            setLoading(false);
            setStreamingId(aiId);
            return;
          }

          if (payload.event === "done") {
            rp.totalTime = data.total_time;
            rp.reportTitle = data.report_title;
            rp.roundsCompleted = data.rounds_completed;
            pushStageLog("深度研究完成，已生成最终文档", { step: "done" });
            setMessages((prev) => prev.map((m) =>
              m.id === aiId
                ? { ...m, streaming: false, statusText: undefined, researchProgress: { ...rp } }
                : m
            ));
            setHistory((prev) => [{
              id: aiId,
              title: question.slice(0, 30) + (question.length > 30 ? "..." : ""),
              preview: fullContent.slice(0, 60) + (fullContent.length > 60 ? "..." : ""),
              time: new Date().toISOString(),
              group: "今天",
              question: `🔬 ${question}`,
              answer: fullContent,
            }, ...prev]);
            setStreamingId(null);
            setLoading(false);
            return;
          }

          if (payload.event === "error") {
            setMessages((prev) => prev.map((m) =>
              m.id === aiId ? { ...m, content: `研究出错: ${data.message}`, streaming: false, statusText: undefined } : m
            ));
            setStreamingId(null);
            setLoading(false);
          }
        },
      );

      setMessages((prev) => prev.map((m) =>
        m.id === aiId ? { ...m, streaming: false, researchProgress: { ...rp } } : m
      ));
    } catch (err) {
      setStreamingId(null);
      setLoading(false);
      setMessages((prev) => prev.map((m) =>
        m.id === aiId ? { ...m, content: `请求失败: ${(err as Error).message}`, streaming: false, statusText: undefined } : m
      ));
    }
  };

  const toggleDeepResearchMode = () => {
    setSelectedSource(null);
    setDeepResearchMode((prev) => {
      const next = !prev;
      if (!next) setHasShownDeepResearchGuide(false);
      return next;
    });
  };

  const handleSourceSelect = (src: Source) => { setSelectedSource((prev) => prev?.name === src.name ? null : src); };

  const sessionHistoryEntries = Object.values(
    history.reduce((acc: Record<string, HistoryEntry[]>, item) => {
      const key = item.sessionId || item.id;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {})
  )
    .map((items) => items.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()))
    .map((items) => {
      const first = items[0];
      const last = items[items.length - 1];
      return {
        ...last,
        title: first.question.slice(0, 30) + (first.question.length > 30 ? "..." : ""),
        preview: last.answer.slice(0, 60) + (last.answer.length > 60 ? "..." : ""),
        group: formatHistoryGroup(last.time),
        sessionTurns: items,
      };
    })
    .filter((item) => {
      const q = historySearch.trim().toLowerCase();
      if (!q) return true;
      return item.title.toLowerCase().includes(q) || item.preview.toLowerCase().includes(q);
    })
    // 最新的一条对话排在最前，保证会话列表按最近活动倒序展示
    .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());

  const groupedHistory = sessionHistoryEntries.reduce((acc: Record<string, Array<HistoryEntry & { sessionTurns: HistoryEntry[] }>>, item) => {
    if (!acc[item.group]) acc[item.group] = [];
    acc[item.group].push(item);
    return acc;
  }, {});

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-56px-48px)] gap-4 min-h-0">
      {/* History Sidebar — 移动端顶部收起，桌面端左侧常驻 */}
      {historyOpen && (
        <div className="w-full max-h-48 lg:max-h-none lg:w-64 flex-shrink-0 bg-white rounded-xl border border-gray-100 flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-[#1A2B3C]">历史对话</span>
            <button onClick={() => setHistoryOpen(false)} className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded cursor-pointer"><RIcon name="close-line" className="text-[#718096] text-sm" /></button>
          </div>
          <div className="px-3 py-2.5 border-b border-gray-100">
            <div className="flex items-center gap-2 bg-[#F4F8FB] rounded-lg px-3 h-8 focus-within:ring-1 focus-within:ring-[#0EA5A0] transition-all">
              <RIcon name="search-line" className="text-[#718096] text-sm" />
              <input type="text" placeholder="搜索历史记录..." className="flex-1 text-xs bg-transparent outline-none placeholder:text-gray-400 text-[#4A5568]" value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} />
              {historySearch && (<button onClick={() => setHistorySearch("")} className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0"><RIcon name="close-circle-fill" className="text-sm" /></button>)}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-3">
            {Object.keys(groupedHistory).length === 0 ? (
              <div className="py-10 text-center"><RIcon name="search-line" className="text-2xl text-gray-300" /><p className="text-xs text-[#718096] mt-2">{historySearch ? "未找到匹配记录" : "暂无历史对话"}</p></div>
            ) : (
              Object.entries(groupedHistory).map(([group, items]) => (
                <div key={group}>
                  <p className="text-xs text-[#718096] px-2 py-1 font-medium">{group}</p>
                  {items.map((item) => (
                    <button key={item.id} onClick={() => {
                      const turns = item.sessionTurns || [item];
                      setCurrentSessionId(item.sessionId || null);
                      setCurrentParentId(Number(turns[turns.length - 1]?.id || 0) || null);
                      setChatMessages(
                        turns.flatMap((turn) => {
                          const ts = formatBeijingTime(turn.time);
                          return [
                            { id: `u-${turn.id}`, role: "user", content: turn.question, time: ts },
                            { id: `ai-${turn.id}`, role: "ai", content: turn.answer, time: ts, sources: turn.sources || [] },
                          ];
                        })
                      );
                    }} className="w-full text-left px-3 py-2.5 rounded-lg transition-colors group cursor-pointer hover:bg-gray-50 text-[#4A5568]">
                      <p className="text-xs font-medium truncate mb-0.5">{historySearch ? <HighlightText text={item.title} keyword={historySearch} /> : item.title}</p>
                      <p className="text-xs text-[#718096] truncate">{historySearch ? <HighlightText text={item.preview} keyword={historySearch} /> : item.preview}</p>
                      <span className="text-xs text-[#718096]/60 mt-1 block">{formatBeijingMonthDayTime(item.time)}</span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 bg-white rounded-xl border border-gray-100 flex overflow-hidden min-w-0 min-h-0">
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Header */}
          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-[#0EA5A0] to-[#06B6D4] rounded-lg flex items-center justify-center"><RIcon name="chat-ai-line" className="text-white text-sm" /></div>
              <div>
                <h1 className="text-sm font-semibold text-[#1A2B3C]">AI智能问答</h1>
                <p className="text-xs text-[#718096]">基于企业知识库 · 逐文件精准分析</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {selectedSource && (<div className="flex items-center gap-1.5 px-2.5 h-7 bg-[#0EA5A0]/10 rounded-lg"><RIcon name="eye-line" className="text-[#0EA5A0] text-xs" /><span className="text-xs text-[#0EA5A0] font-medium whitespace-nowrap">文档预览</span></div>)}
              <button onClick={() => { if (abortRef.current) abortRef.current.abort(); setMessages([]); setStreamingId(null); setSelectedSource(null); setLoading(false); setCurrentSessionId(null); setCurrentParentId(null); }} className="flex items-center gap-1.5 px-3 h-8 rounded-lg border border-gray-200 text-xs text-[#4A5568] hover:border-[#0EA5A0] hover:text-[#0EA5A0] transition-colors cursor-pointer whitespace-nowrap"><RIcon name="add-line" />新建对话</button>
              <button onClick={() => setHistoryOpen(!historyOpen)} className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors cursor-pointer ${historyOpen ? "border-[#0EA5A0] bg-[#0EA5A0]/10 text-[#0EA5A0]" : "border-gray-200 text-[#718096] hover:border-[#0EA5A0]"}`}><RIcon name="history-line" className="text-sm" /></button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-5 space-y-6">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-16 h-16 bg-[#0EA5A0]/10 rounded-2xl flex items-center justify-center mb-4"><RIcon name="chat-ai-line" className="text-3xl text-[#0EA5A0]" /></div>
                <h3 className="text-base font-semibold text-[#1A2B3C] mb-1">开始新对话</h3>
                <p className="text-sm text-[#718096] max-w-sm">AI将基于您权限范围内的文档为您解答，每个文件独立分析并实时展示结果</p>
              </div>
            )}

            {messages.map((msg) => (
              <div key={msg.id} className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}>
                {msg.role === "ai" ? (
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0EA5A0] to-[#06B6D4] flex items-center justify-center flex-shrink-0"><i className="ri-ai-generate text-white text-sm" /></div>
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#0EA5A0] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">我</div>
                )}

                <div className={`max-w-[75%] ${msg.role === "user" ? "items-end" : "items-start"} flex flex-col gap-1`}>
                  {msg.role === "user" ? (
                    <div className="rounded-2xl px-4 py-3 bg-[#0EA5A0] text-white rounded-tr-sm">
                      <p className="text-sm leading-relaxed">{msg.content}</p>
                    </div>
                  ) : (
                    <div className="w-full">
                      {msg.researchProgress && (
                        <div className="mb-2 space-y-2">
                          {msg.statusText && (
                            <div className="flex items-center gap-2 text-xs text-purple-500">
                              <RIcon name="loader-4-line" className="animate-spin" />
                              {msg.statusText}
                            </div>
                          )}

                          {!!msg.researchProgress.stageLogs?.length && (
                            <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-3">
                              <p className="mb-2 text-xs font-medium text-indigo-600">
                                <RIcon name="timer-flash-line" className="mr-1" />
                                研究阶段日志
                              </p>
                              <div className="space-y-1.5 max-h-36 overflow-y-auto">
                                {msg.researchProgress.stageLogs.slice(-8).map((log, index) => (
                                  <div key={`${log.at}-${index}`} className="text-xs text-[#4A5568]">
                                    <span className="text-[#718096] mr-1">[{log.at}]</span>
                                    {log.message}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {!!msg.researchProgress.thinkingText && (
                            <div className="rounded-xl border border-amber-100 bg-amber-50 p-3">
                              <p className="mb-2 text-xs font-medium text-amber-600">
                                <RIcon name="brain-line" className="mr-1" />
                                模型思考中{msg.researchProgress.thinkingPhase ? ` · ${msg.researchProgress.thinkingPhase}` : ""}
                              </p>
                              <div className="max-h-40 overflow-y-auto whitespace-pre-wrap text-xs leading-relaxed text-[#4A5568]">
                                {msg.researchProgress.thinkingText}
                              </div>
                            </div>
                          )}

                          {!!msg.researchProgress.subQuestions?.length && (
                            <div className="rounded-xl border border-purple-200 bg-purple-50 p-3">
                              <p className="mb-2 text-xs font-medium text-purple-500">
                                <RIcon name="git-branch-line" className="mr-1" />
                                已拆解为 {msg.researchProgress.subQuestions.length} 个研究子问题
                              </p>
                              <div className="space-y-1">
                                {msg.researchProgress.subQuestions.map((q, i) => {
                                  const finding = msg.researchProgress?.findings?.filter((f) => f.subQuestion === q).slice(-1)[0];
                                  const isActive = !finding && msg.researchProgress?.currentSubQuestion === q && !!msg.streaming;
                                  return (
                                    <div key={i} className={`flex items-start gap-2 text-xs ${finding ? "text-emerald-600" : isActive ? "text-purple-500" : "text-[#718096]"}`}>
                                      {finding ? (
                                        <RIcon name="checkbox-circle-fill" className="mt-0.5 flex-shrink-0" />
                                      ) : isActive ? (
                                        <RIcon name="loader-4-line" className="animate-spin mt-0.5 flex-shrink-0" />
                                      ) : (
                                        <RIcon name="checkbox-blank-circle-line" className="mt-0.5 flex-shrink-0" />
                                      )}
                                      <span>{q}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {!!msg.researchProgress.currentQueries?.length && (
                            <div className="rounded-xl border border-cyan-100 bg-cyan-50 p-3">
                              <p className="mb-2 text-xs font-medium text-cyan-600">
                                <RIcon name="search-line" className="mr-1" />
                                当前搜索查询
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {msg.researchProgress.currentQueries.map((query) => (
                                  <span key={query} className="rounded-full border border-cyan-200 bg-white px-2 py-1 text-xs text-cyan-600">
                                    {query}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {!!msg.researchProgress.findings?.length && (
                            <div className="rounded-xl border border-gray-100 bg-[#F4F8FB] p-3">
                              <p className="mb-2 text-xs font-medium text-[#1A2B3C]">
                                <RIcon name="file-search-line" className="mr-1 text-[#0EA5A0]" />
                                阶段性发现
                              </p>
                              <div className="space-y-2">
                                {msg.researchProgress.findings.slice(-3).map((finding, index) => (
                                  <div key={`${finding.subQuestion}-${finding.round}-${index}`} className="text-xs">
                                    <p className="font-medium text-[#1A2B3C]">
                                      第 {finding.round}/{finding.totalRounds} 轮 · {finding.subQuestion}
                                    </p>
                                    <p className="mt-1 text-[#718096]">{finding.summary}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {!!msg.researchProgress.searchRounds?.length && (
                            <div className="rounded-xl border border-sky-100 bg-sky-50 p-3">
                              <p className="mb-2 text-xs font-medium text-sky-600">
                                <RIcon name="route-line" className="mr-1" />
                                多轮搜索过程
                              </p>
                              <div className="space-y-3">
                                {msg.researchProgress.searchRounds.map((roundItem, index) => (
                                  <div key={`${roundItem.subQuestion}-${roundItem.round}-${index}`} className="rounded-lg border border-sky-100 bg-white p-2.5">
                                    <p className="text-xs font-medium text-[#1A2B3C]">
                                      第 {roundItem.round} 轮 · {roundItem.subQuestion}
                                    </p>
                                    {!!roundItem.focus && (
                                      <p className="mt-1 text-xs text-[#718096]">目标：{roundItem.focus}</p>
                                    )}
                                    {!!roundItem.queries?.length && (
                                      <div className="mt-2 flex flex-wrap gap-1.5">
                                        {roundItem.queries.map((query) => (
                                          <span key={query} className="rounded-full border border-sky-200 bg-sky-50 px-2 py-1 text-xs text-sky-700">
                                            {query}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    {!!roundItem.results?.length && (
                                      <div className="mt-2 space-y-2">
                                        {roundItem.results.map((queryResult) => (
                                          <div key={queryResult.query}>
                                            <p className="text-xs font-medium text-[#4A5568]">{queryResult.query}</p>
                                            <div className="mt-1 space-y-1">
                                              {queryResult.results.slice(0, 3).map((result) => (
                                                <div key={`${queryResult.query}-${result.url}`} className="rounded border border-sky-100 bg-sky-50/40 p-2">
                                                  <a
                                                    href={result.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-xs font-medium text-[#1A2B3C] hover:text-sky-600"
                                                  >
                                                    {result.title || result.url}
                                                  </a>
                                                  {!!result.snippet && (
                                                    <p className="mt-1 text-xs text-[#718096] line-clamp-2">{result.snippet}</p>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {!!roundItem.summary && (
                                      <p className="mt-2 text-xs text-[#4A5568]">阶段总结：{roundItem.summary}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {!!msg.researchProgress.webResults?.length && (
                            <div className="rounded-xl border border-orange-100 bg-orange-50 p-3">
                              <p className="mb-2 text-xs font-medium text-orange-500">
                                <RIcon name="global-line" className="mr-1" />
                                最近网络命中
                              </p>
                              <div className="space-y-2">
                                {msg.researchProgress.webResults.slice(-2).map((item) => (
                                  <div key={item.query} className="text-xs">
                                    <p className="font-medium text-[#1A2B3C]">{item.query}</p>
                                    <p className="text-[#718096] mb-1">命中 {item.results.length} 条结果</p>
                                    <div className="space-y-1">
                                      {item.results.slice(0, 3).map((result) => (
                                        <div key={result.url} className="rounded-lg bg-white/80 border border-orange-100 p-2">
                                          <a
                                            href={result.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="block font-medium text-[#1A2B3C] hover:text-orange-500"
                                          >
                                            {result.title || result.url}
                                          </a>
                                          {!!result.snippet && (
                                            <p className="mt-1 text-[#718096] line-clamp-2">{result.snippet}</p>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {!!msg.researchProgress.totalTime && !msg.streaming && (
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-xs text-[#718096]">
                                <RIcon name="time-line" className="mr-1" />
                                研究耗时 {msg.researchProgress.totalTime}s，完成 {msg.researchProgress.roundsCompleted || 0} 轮分析
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* RAG Progress Steps */}
                      {msg.progressSteps && msg.progressSteps.length > 0 && (
                        <CollapsibleProgressSteps steps={msg.progressSteps} streaming={!!msg.streaming} />
                      )}

                      {/* Per-file answer cards */}
                      {msg.fileAnswers && msg.fileAnswers.length > 0 && (
                        <div className="mb-2">
                          <div className="flex items-center gap-1.5 mb-2">
                            <RIcon name="file-search-line" className="text-[#0EA5A0] text-sm" />
                            <span className="text-xs font-medium text-[#718096]">逐文件分析 ({msg.fileAnswers.length} 个文件)</span>
                          </div>
                          {msg.fileAnswers.map((fa) => (
                            <FileAnswerCard key={fa.file_id} fa={fa} defaultOpen={true} />
                          ))}
                        </div>
                      )}

                      {/* Synthesis / main answer — 仅当以下情况显示：
                          (a) 没有逐文件卡片（material_lookup / 单条回答路径），
                          或 (b) 逐文件 ≥2 且存在综合分析。避免单文件时卡片和正文重复 */}
                      {(
                        (!msg.fileAnswers?.length && (msg.content || !msg.streaming)) ||
                        (msg.fileAnswers && msg.fileAnswers.length >= 2 && msg.hasSynthesis && msg.content)
                      ) && (
                        <div className="rounded-2xl px-4 py-3 bg-[#F4F8FB] rounded-tl-sm">
                          {msg.hasSynthesis && msg.fileAnswers && msg.fileAnswers.length >= 2 && (
                            <div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-gray-200">
                              <RIcon name="sparkling-line" className="text-[#0EA5A0] text-sm" />
                              <span className="text-xs font-semibold text-[#1A2B3C]">综合分析</span>
                            </div>
                          )}
                          <MarkdownMessage
                            content={dedupSourceFilenames(msg.content)}
                            sources={[
                              ...(msg.sources || []),
                              // 逐文件分析里的 file_id/file_name 也作为 fallback linkify 源
                              ...((msg.fileAnswers || []).map((fa) => ({
                                file_id: fa.file_id,
                                name: fa.file_name,
                                type: fa.file_name.split(".").pop() || "",
                              }))),
                            ]}
                          />
                          {msg.streaming && msg.content && (
                            <span className="inline-block w-0.5 h-4 bg-[#0EA5A0] rounded-full ml-0.5 align-middle" style={{ animation: "cursorBlink 0.8s steps(1) infinite" }} />
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {msg.role === "ai" && msg.sources && msg.sources.length > 0 && (
                    <>
                      <SourceTags sources={msg.sources} visible={!msg.streaming} onSelect={handleSourceSelect} selectedName={selectedSource?.name} />
                      {!msg.streaming && msg.sources.some((s) => s.relevance !== undefined) && (
                        <p className="text-[11px] text-[#718096] mt-1.5">
                          说明：右上角的百分比为「相关度」，由向量相似度计算，数值越高表示该文件与问题越相关。
                        </p>
                      )}
                    </>
                  )}
                  <span className="text-xs text-[#718096]">{msg.time}</span>
                </div>
              </div>
            ))}

            {/* Loading spinner fallback - shown briefly before progress events arrive */}
            {loading && !messages[messages.length - 1]?.progressSteps?.length && (
              <div className="flex justify-center py-2">
                <div className="flex gap-1 items-center h-5">
                  {[0, 150, 300].map((delay) => (<span key={delay} className="w-1.5 h-1.5 bg-[#0EA5A0] rounded-full animate-bounce" style={{ animationDelay: `${delay}ms` }} />))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input area */}
          <div className="px-5 pb-5 pt-3 border-t border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <button onClick={() => setFilterOpen(!filterOpen)} className={`flex items-center gap-1 px-2.5 h-7 rounded-lg text-xs transition-colors cursor-pointer border ${filterOpen || selectedFileIds.length > 0 ? "border-[#0EA5A0] bg-[#0EA5A0]/10 text-[#0EA5A0]" : "border-gray-200 text-[#718096] hover:border-[#0EA5A0]"}`}>
                <RIcon name="filter-3-line" />筛选文件
                {selectedFileIds.length > 0 && (<span className="bg-[#0EA5A0] text-white rounded-full px-1.5 text-[10px] font-medium ml-0.5">{selectedFileIds.length}</span>)}
              </button>
              {selectedFileIds.length > 0 && (
                <button onClick={() => { setFilterJobTypeId(undefined); setFilterWorkStageId(undefined); setFilterProjectId(undefined); setFilterCategory(""); setSelectedFileIds([]); setFilteredFiles([]); setFilterOpen(false); }} className="flex items-center gap-1 px-2 h-7 text-xs text-red-500 hover:bg-red-50 rounded-lg cursor-pointer"><RIcon name="close-line" />清除筛选</button>
              )}
              <button
                onClick={() => setFilesListOpen(!filesListOpen)}
                className="flex items-center gap-1 text-xs text-[#718096] ml-auto hover:text-[#0EA5A0] cursor-pointer transition-colors"
              >
                <RIcon name="database-2-line" />
                {selectedFileIds.length > 0 ? (<>筛选 <strong className="text-[#0EA5A0]">{selectedFileIds.length}</strong> 个文件</>) : (<>全部 <strong className="text-[#0EA5A0]">{fileCount || "..."}</strong> 个文件</>)}
                <i className={`ri-arrow-${filesListOpen ? "up" : "down"}-s-line`} />
              </button>
            </div>
            {filterOpen && (
              <div className="mb-3 p-3 bg-[#F4F8FB] rounded-xl space-y-2.5">
                <div className="flex gap-3 flex-wrap">
                  <div className="flex-1 min-w-[120px]">
                    <label className="text-xs text-[#718096] mb-1 block">按部门</label>
                    <Select
                      size="small" showSearch allowClear placeholder="全部部门" style={{ width: "100%" }}
                      value={filterDepartmentId}
                      onChange={(v) => setFilterDepartmentId(v as number | undefined)}
                      optionFilterProp="label"
                      options={departmentOptions.map((d) => ({ value: d.id, label: d.name }))}
                    />
                  </div>
                  <div className="flex-1 min-w-[120px]">
                    <label className="text-xs text-[#718096] mb-1 block">按工种</label>
                    <Select
                      size="small" showSearch allowClear placeholder="全部工种" style={{ width: "100%" }}
                      value={filterJobTypeId}
                      onChange={(v) => setFilterJobTypeId(v as number | undefined)}
                      optionFilterProp="label"
                      options={jobTypeOptions.map((jt) => ({ value: jt.id, label: jt.name }))}
                    />
                  </div>
                  <div className="flex-1 min-w-[120px]">
                    <label className="text-xs text-[#718096] mb-1 block">按业务阶段</label>
                    <Select
                      size="small" showSearch allowClear placeholder="全部阶段" style={{ width: "100%" }}
                      value={filterWorkStageId}
                      onChange={(v) => setFilterWorkStageId(v as number | undefined)}
                      optionFilterProp="label"
                      options={workStageOptions.map((s) => ({ value: s.id, label: s.name }))}
                    />
                  </div>
                  <div className="flex-1 min-w-[120px]">
                    <label className="text-xs text-[#718096] mb-1 block">按项目</label>
                    <Select
                      size="small" showSearch allowClear placeholder="全部项目" style={{ width: "100%" }}
                      value={filterProjectId}
                      onChange={(v) => setFilterProjectId(v as number | undefined)}
                      optionFilterProp="label"
                      options={projectOptions.map((p) => ({ value: p.id, label: p.name }))}
                    />
                  </div>
                  <div className="flex-1 min-w-[120px]">
                    <label className="text-xs text-[#718096] mb-1 block">按分类</label>
                    <Select
                      size="small" showSearch allowClear placeholder="全部分类" style={{ width: "100%" }}
                      value={filterCategory || undefined}
                      onChange={(v) => setFilterCategory((v ?? "") as string)}
                      optionFilterProp="label"
                      options={categoryOptions.map((cat) => ({ value: cat, label: cat }))}
                    />
                  </div>
                </div>
                {filteredFiles.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
                    {filteredFiles.map((f) => (
                      <label key={f.id} className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs cursor-pointer transition-colors border ${selectedFileIds.includes(f.id) ? "bg-[#0EA5A0]/10 border-[#0EA5A0] text-[#0EA5A0]" : "bg-white border-gray-200 text-[#4A5568]"}`}>
                        <input type="checkbox" className="hidden" checked={selectedFileIds.includes(f.id)} onChange={() => setSelectedFileIds((prev) => prev.includes(f.id) ? prev.filter((id) => id !== f.id) : [...prev, f.id])} />
                        <RIcon name="file-line" className={`${selectedFileIds.includes(f.id) ? "text-[#0EA5A0]" : "text-[#718096]"}`} />
                        <span className="max-w-28 truncate">{f.file_name}</span>
                      </label>
                    ))}
                  </div>
                )}
                {(filterDepartmentId || filterJobTypeId || filterWorkStageId || filterProjectId || filterCategory) && filteredFiles.length === 0 && (<p className="text-xs text-[#718096] text-center py-2">未找到匹配的文件</p>)}
              </div>
            )}
            {/* 文件列表展开 */}
            {filesListOpen && (
              <div className="mb-2 p-3 bg-[#F4F8FB] rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-[#1A2B3C]">
                    {selectedFileIds.length > 0 ? `已筛选 ${selectedFileIds.length} 个文件` : `全部 ${allFiles.length} 个文件`}
                  </span>
                  <button onClick={() => setFilesListOpen(false)} className="text-xs text-[#718096] hover:text-[#1A2B3C] cursor-pointer"><RIcon name="close-line" /></button>
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                  {(selectedFileIds.length > 0 ? allFiles.filter((f) => selectedFileIds.includes(f.id)) : allFiles).map((f) => {
                    const ext = f.file_type?.toLowerCase() || "";
                    return (
                      <span key={f.id} className="flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded-lg text-xs text-[#4A5568] hover:border-[#0EA5A0] transition-colors">
                        <RIcon name="file-line" className={`${fileTypeIcon[ext] || ""} text-[#0EA5A0]`} />
                        <span className="max-w-40 truncate">{f.file_name}</span>
                        <span className="text-[10px] text-[#718096]">{f.index_status === "completed" ? "已索引" : getIndexStatusLabel(f.index_status)}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex gap-2 items-end">
              <div className="flex-1 border border-gray-200 rounded-xl focus-within:border-[#0EA5A0] transition-colors bg-white">
                <textarea rows={2} placeholder={deepResearchMode ? "输入研究主题、目标和关注点，系统会自动开启多轮 DeepResearch..." : "输入您的问题，按 Enter 发送，Shift+Enter 换行..."} className="w-full px-4 py-3 text-sm bg-transparent outline-none resize-none text-[#1A2B3C] placeholder:text-gray-400" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); deepResearchMode ? handleDeepResearch() : handleSend(); } }} />
              </div>
              {(loading || streamingId) ? (
                <button
                  onClick={() => { if (abortRef.current) abortRef.current.abort(); setLoading(false); setStreamingId(null); }}
                  className="w-10 h-10 bg-red-500 hover:bg-red-600 text-white rounded-xl flex items-center justify-center transition-all flex-shrink-0 cursor-pointer"
                  title="暂停回答"
                >
                  <RIcon name="stop-fill" className="text-sm" />
                </button>
              ) : (
                <button onClick={deepResearchMode ? handleDeepResearch : handleSend} disabled={!input.trim()} className={`w-10 h-10 ${deepResearchMode ? "bg-purple-500 hover:bg-purple-600" : "bg-[#0EA5A0] hover:bg-[#0D9590]"} disabled:opacity-40 text-white rounded-xl flex items-center justify-center transition-all flex-shrink-0 cursor-pointer`}><RIcon name="microscope-line" className={`${deepResearchMode ? "" : ""} text-sm`} /></button>
              )}
            </div>
            <div className="mt-2 flex items-center justify-between gap-3">
              <p className="text-xs text-[#718096]">
                {deepResearchMode
                  ? `DeepResearch 已开启：自动拆题、多轮联网搜索、汇总研究文档${selectedFileIds.length > 0 ? `，并结合 ${selectedFileIds.length} 个已选文件` : ""}`
                  : `基于权限范围内文件检索${selectedFileIds.length > 0 ? `（已选 ${selectedFileIds.length} 个文件）` : ""}`}
              </p>
              <button
                onClick={toggleDeepResearchMode}
                className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${deepResearchMode ? "border-purple-200 bg-purple-50 text-purple-500" : "border-gray-200 text-[#718096] hover:border-purple-200 hover:text-purple-500"}`}
              >
                <RIcon name="microscope-line" />
                深度研究
                {deepResearchMode && <RIcon name="check-line" />}
              </button>
            </div>
            <style>{`@keyframes cursorBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
          </div>
        </div>
        {selectedSource && (<DocPreviewPanel source={selectedSource} onClose={() => setSelectedSource(null)} />)}
      </div>
    </div>
  );
}
